/**
 * SharePackControls
 *
 * Authenticated component used by PackProgressPage. Surfaces the
 * "Share with solicitor" button (Requirement 26.1), the resulting URL
 * with copy-to-clipboard (26.2), and a "Revoke link" action (26.3).
 *
 * Requirement 26.4: before generating a link the user MUST have
 * `User.firstName` set. We fetch GET /api/user/profile on mount; if
 * `firstName` is null we render an inline prompt and POST it to
 * /api/user/profile before enabling the generate button.
 */

import { useEffect, useState, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ShareLinkSummary {
  shareId: string;
  url: string;
  createdAt: string;
  expiresAt: string;
}

interface UserProfile {
  firstName: string | null;
  email: string;
}

export interface SharePackControlsProps {
  packId: string;
  /** Whether the pack has any confirmed fields (Requirement 26.1). */
  canShare: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'error'; message: string };

export function SharePackControls({ packId, canShare }: SharePackControlsProps) {
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [share, setShare] = useState<ShareLinkSummary | null>(null);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch user profile to check firstName state.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }
        const data = (await response.json()) as UserProfile;
        if (!cancelled) {
          setProfile(data);
          setLoadState({ kind: 'ready' });
        }
      } catch {
        if (!cancelled) {
          setLoadState({
            kind: 'error',
            message: 'Could not load your profile. Refresh the page and try again.',
          });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSetFirstName = useCallback(async (firstName: string) => {
    setActionError(null);
    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const message =
        (data as { error?: string }).error ||
        'Could not save your first name. Please try again.';
      throw new Error(message);
    }
    setProfile((prev) => (prev ? { ...prev, firstName } : { firstName, email: '' }));
  }, []);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/compliance-packs/${encodeURIComponent(packId)}/share`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (response.status === 400) {
        const data = await response.json().catch(() => ({}));
        if ((data as { error?: string }).error === 'firstName required') {
          setProfile((prev) => (prev ? { ...prev, firstName: null } : prev));
          setActionError('Please set your first name before sharing.');
          return;
        }
      }
      if (!response.ok) {
        throw new Error('Failed to create share link.');
      }
      const data = (await response.json()) as ShareLinkSummary;
      setShare(data);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create share link.');
    } finally {
      setGenerating(false);
    }
  }, [packId]);

  const handleRevoke = useCallback(async () => {
    if (!share) return;
    setRevoking(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/compliance-packs/${encodeURIComponent(packId)}/share/${encodeURIComponent(share.shareId)}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to revoke share link.');
      }
      setShare(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to revoke share link.');
    } finally {
      setRevoking(false);
    }
  }, [packId, share]);

  const handleCopy = useCallback(async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Some test environments don't support clipboard. Fall back silently.
    }
  }, [share]);

  if (loadState.kind === 'loading') {
    return (
      <section
        aria-labelledby="share-controls-heading"
        className="rounded-lg border border-slate-200 bg-white p-6"
      >
        <h2 id="share-controls-heading" className="text-lg font-semibold text-slate-950">
          Share with your solicitor
        </h2>
        <p className="mt-2 text-slate-700">Loading…</p>
      </section>
    );
  }

  if (loadState.kind === 'error') {
    return (
      <section
        aria-labelledby="share-controls-heading"
        className="rounded-lg border border-slate-200 bg-white p-6"
      >
        <h2 id="share-controls-heading" className="text-lg font-semibold text-slate-950">
          Share with your solicitor
        </h2>
        <p className="error" role="alert">
          {loadState.message}
        </p>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="share-controls-heading"
      className="rounded-lg border border-slate-200 bg-white p-6"
    >
      <h2 id="share-controls-heading" className="text-lg font-semibold text-slate-950">
        Share with your solicitor
      </h2>
      <p className="mt-2 text-slate-700">
        Generate a read-only link your solicitor can use to see your pack progress.
        They will not see your answers, your documents, or the final PDF.
      </p>

      {!canShare && (
        <p className="mt-4 text-sm text-slate-700">
          You can share once you have confirmed at least one answer.
        </p>
      )}

      {canShare && profile && !profile.firstName && (
        <FirstNamePrompt onSubmit={handleSetFirstName} />
      )}

      {canShare && profile && profile.firstName && !share && (
        <div className="mt-4">
          <button
            type="button"
            className="btn-primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating…' : 'Generate share link'}
          </button>
        </div>
      )}

      {share && (
        <div className="mt-4 space-y-3">
          <p className="text-sm font-semibold text-slate-950">Active share link</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              readOnly
              value={share.url}
              aria-label="Share link URL"
              className="input"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" className="btn-secondary" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-sm text-slate-700">
            Created: {new Date(share.createdAt).toLocaleString('en-GB')}<br />
            Expires: {new Date(share.expiresAt).toLocaleString('en-GB')}
          </p>
          <button
            type="button"
            className="btn-ghost text-red-700 border border-red-300 hover:bg-red-50"
            onClick={handleRevoke}
            disabled={revoking}
          >
            {revoking ? 'Revoking…' : 'Revoke link'}
          </button>
        </div>
      )}

      {actionError && (
        <p className="error mt-4" role="alert">
          {actionError}
        </p>
      )}
    </section>
  );
}

interface FirstNamePromptProps {
  onSubmit: (firstName: string) => Promise<void>;
}

function FirstNamePrompt({ onSubmit }: FirstNamePromptProps) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!value.trim()) {
      setError('Enter your first name.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(value.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your first name.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handle}
      className="mt-4 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-4 space-y-3"
      aria-labelledby="first-name-prompt-heading"
    >
      <p id="first-name-prompt-heading" className="text-sm font-semibold text-slate-950">
        Add your first name to share
      </p>
      <p className="text-sm text-slate-800">
        Your solicitor will see your first name on the shared link. We do not show your
        full name, email, or address.
      </p>
      <label className="label" htmlFor="first-name-input">
        First name
      </label>
      <input
        id="first-name-input"
        className="input"
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="given-name"
        maxLength={60}
        required
      />
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? 'Saving…' : 'Save and continue'}
      </button>
    </form>
  );
}

export default SharePackControls;
